import {
  ChatCircleDots,
  EnvelopeSimple,
  FilePdf,
  Heart,
  Ruler,
} from "@phosphor-icons/react";
import { API } from "@/lib/api";

import { PhotoStrip } from "@/components/site/PhotoStrip";

// One slab on the floor under a public product: photo, item facts and the action row
export const SlabRow = ({
  item,
  s,
  email,
  saved,
  onMeasure,
  onInquire,
  onWish,
  onZoom,
}) => {
  const photoUrl = (path, w) =>
    `${API}/public/showroom/photo/${path}${w ? `?w=${w}` : ""}`;
  const mail = `mailto:${email || ""}?subject=${encodeURIComponent(
    `Slab enquiry — ${item.color} ${s.item_no || ""}`.trim(),
  )}&body=${encodeURIComponent(
    `I would like more information on ${item.color} (item ${
      s.item_no || item.code
    }, ${s.size}).`,
  )}`;

  return (
    <div
      data-testid={`public-item-slab-${s.id}`}
      className="s-card flex min-w-0 flex-col gap-4 lg:flex-row lg:items-center"
    >
      <div className="s-fig s-card-fig h-24 w-full shrink-0 overflow-hidden lg:w-36">
        {(s.photos || []).length > 1 ? (
          <PhotoStrip
            photos={s.photos}
            srcOf={(path) => photoUrl(path, 400)}
            onOpen={(n) => onZoom?.(s, n)}
            testPrefix={`public-item-slab-zoom-${s.id}`}
          />
        ) : s.thumb ? (
          <button
            type="button"
            data-testid={`public-item-slab-zoom-${s.id}`}
            onClick={() => onZoom?.(s, 0)}
            title="Zoom the slab photo"
            className="block h-full w-full cursor-zoom-in"
          >
            <img
              src={photoUrl(s.thumb, 400)}
              alt=""
              className="h-full w-full object-cover"
            />
          </button>
        ) : null}
      </div>
      <div className="min-w-0 flex-1 px-1">
        <div className="truncate text-xl font-semibold tracking-tight">
          Item #{s.item_no || "—"} · {item.color}
        </div>
        <div className="mt-1 flex flex-wrap gap-x-4 gap-y-0.5 text-sm text-[color:var(--s-mute)]">
          <span>
            Material{" "}
            <b className="font-medium text-[color:var(--s-ink)]">
              {s.material || item.kind}
            </b>
          </span>
          <span>
            Thickness{" "}
            <b className="font-medium text-[color:var(--s-ink)]">
              {s.thickness || "—"}
            </b>
          </span>
          <span>
            Size{" "}
            <b className="font-medium text-[color:var(--s-ink)]">
              {s.sqft > 0 ? `${s.size} · ${s.sqft} ft²` : "On request"}
            </b>
          </span>
        </div>
      </div>
      <div
        className="flex flex-wrap gap-2 lg:shrink-0"
        data-testid={`public-item-slab-actions-${s.id}`}
      >
        <a
          href={`${API}/public/showroom/item/${item.key}/pdf?slab=${encodeURIComponent(s.id)}`}
          target="_blank"
          rel="noreferrer"
          data-testid={`public-item-pdf-${s.id}`}
          className="s-act s-act-soft"
        >
          <FilePdf size={16} weight="bold" /> PDF
        </a>
        <button
          type="button"
          data-testid={`public-item-measure-${s.id}`}
          onClick={() => onMeasure(s)}
          disabled={!(s.length_in > 0 && s.width_in > 0)}
          title={
            s.length_in > 0 && s.width_in > 0
              ? "Size a piece on this slab"
              : "Size not on file — ask us"
          }
          className="s-act s-act-soft"
        >
          <Ruler size={16} weight="bold" /> Measure
        </button>
        <button
          type="button"
          data-testid={`public-item-inquire-${s.id}`}
          onClick={() =>
            onInquire(
              `${item.color} · item ${s.item_no || item.code} (${s.size})`,
            )
          }
          className="s-act s-act-gold"
        >
          <ChatCircleDots size={16} weight="bold" /> Inquire
        </button>
        <button
          type="button"
          data-testid={`public-item-wishlist-${s.id}`}
          aria-pressed={saved}
          onClick={() => onWish(s)}
          className={`s-act s-act-soft ${saved ? "s-act-on" : ""}`}
        >
          <Heart size={16} weight={saved ? "fill" : "bold"} />{" "}
          {saved ? "Saved" : "Wishlist"}
        </button>
        <a
          data-testid={`public-item-email-${s.id}`}
          href={mail}
          className="s-act s-act-soft"
        >
          <EnvelopeSimple size={16} weight="bold" /> Email
        </a>
      </div>
    </div>
  );
};

export default SlabRow;
